import React, { useMemo, useState, useEffect } from "react";
import { Skeleton, Table } from "antd";

export default function TableWithSkeleton({
  rowKey,
  rowSelection,
  columns,
  data,
  loading,
  length = 10,
  onChange,
  isCursor = false,
  onRowClick = () => {},
  showIndexColumn = true,
  hiddenColumns = [],
  page = 1,
  pageSize = 10,
  pagination = false,
  currentPage = 1,
  // New expandable props
  expandable = null,
  expandedRowRender = null,
  expandedRowColumns = null,
  expandRowByClick = false,
  defaultExpandAllRows = false,
  expandedRowKeys = null,
  onExpand = null,
  onExpandedRowsChange = null,
  rowExpandable = null,
  indentSize = 15,
  expandIcon = null,
  expandIconColumnIndex = 0,
  sortDirections = false,
  scroll = { x: true },
  className = "table-responsive",
}) {
  const [internalExpandedKeys, setInternalExpandedKeys] = useState([]);

  const filterColumns = useMemo(
    () => columns?.filter((item) => !hiddenColumns?.includes(item?.title)),
    [columns, hiddenColumns],
  );

  const numberColumn = {
    title: "No.",
    dataIndex: "no",
    render: (text, __, index) => {
      return loading ? (
        text
      ) : currentPage !== 1 ? (
        <span className="ps-2"> {currentPage - 10 + index + 1} </span>
      ) : (
        <span className="ps-2"> {(page - 1) * pageSize + index + 1} </span>
      );
    },
    width: 50,
  };

  const newColumns =
    !loading && showIndexColumn
      ? [numberColumn, ...filterColumns]
      : filterColumns;

  // Build skeleton rows synchronously so column switches never show stale rows
  const skeletonData = useMemo(() => {
    const skeletonObj = {};
    filterColumns?.forEach((item) => {
      skeletonObj[item?.dataIndex] = (
        <Skeleton.Input className="table-skeleton" active={true} />
      );
    });
    return Array.from({ length }, (_, index) => ({
      ...skeletonObj,
      key: `skeleton-row-${index}`,
    }));
  }, [filterColumns, length]);

  // Strip fixed/render while loading to avoid Ant Design fixed-column ghost rows
  const skeletonColumns = useMemo(
    () =>
      filterColumns?.map((col) => ({
        title: col.title,
        dataIndex: col.dataIndex,
        width: col.width,
        ellipsis: col.ellipsis,
      })),
    [filterColumns],
  );

  const rowClassName = (record) => {
    return record?.isAdminCommission ? "admin-commission-row" : "";
  };

  // Create skeleton for expanded rows when loading
  const createExpandedRowSkeleton = () => {
    if (!expandedRowColumns || !loading) return null;

    const skeletonExpandedObj = {};
    expandedRowColumns.forEach((item) => {
      skeletonExpandedObj[item?.dataIndex] = (
        <Skeleton.Input className="table-skeleton" active={true} />
      );
    });

    return (
      <div className="expanded-row-skeleton">
        <Table
          columns={expandedRowColumns}
          dataSource={[skeletonExpandedObj, skeletonExpandedObj]}
          pagination={false}
          size="small"
          showHeader={false}
        />
      </div>
    );
  };

  // Handle expand state changes
  const handleExpand = (expanded, record) => {
    const key = record.key || record.userId || record.id;
    if (expanded) {
      setInternalExpandedKeys((prev) => [...prev, key]);
    } else {
      setInternalExpandedKeys((prev) => prev.filter((k) => k !== key));
    }

    if (onExpand) {
      onExpand(expanded, record);
    }
  };

  const handleExpandedRowsChange = (keys) => {
    setInternalExpandedKeys(keys);
    if (onExpandedRowsChange) {
      onExpandedRowsChange(keys);
    }
  };

  useEffect(() => {
    if (!loading && data?.length) {
      const allKeys = data.map((row) => row.key || row.userId || row.id);
      setInternalExpandedKeys(allKeys);
    }
  }, [loading, data]);

  // Handle expandable configuration
  const expandableConfig =
    expandable ||
    (expandedRowRender && {
      expandedRowRender: loading
        ? () => createExpandedRowSkeleton()
        : expandedRowRender,
      expandRowByClick,
      defaultExpandAllRows,
      expandedRowKeys: loading
        ? []
        : expandedRowKeys !== null
          ? expandedRowKeys
          : internalExpandedKeys,
      onExpand: handleExpand,
      onExpandedRowsChange: handleExpandedRowsChange,
      rowExpandable: rowExpandable || (() => true),
      indentSize,
      expandIcon,
      expandIconColumnIndex,
    });

  return (
    <Table
      key={loading ? `skeleton-${length}-${filterColumns?.length}` : "table-data"}
      className={className}
      scroll={scroll === false ? undefined : scroll}
      rowSelection={loading ? null : rowSelection}
      sortDirections={sortDirections}
      columns={loading ? skeletonColumns : newColumns}
      dataSource={loading ? skeletonData : data}
      pagination={pagination}
      onChange={onChange}
      rowKey={
        typeof rowKey === "function"
          ? rowKey
          : (row) =>
              (typeof rowKey === "string" ? row?.[rowKey] : undefined) ||
              row?.key ||
              row?.id
      }
      rowClassName={rowClassName}
      expandable={loading ? undefined : expandableConfig}
      onRow={(record) => ({
        onClick: () => onRowClick(record),
        ...(isCursor && { style: { cursor: "pointer" } }),
      })}
    />
  );
}
